import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hubLogin } from "../services/authService";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import Logo from "../assets/logos/logo.png";
import LogoText from "../assets/logos/logo_text.svg";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await hubLogin(email, password);
      if (res.isSuccess) {
        login(res.data);
        navigate("/", { replace: true });
      } else {
        setError(res.message ?? "Invalid credentials.");
      }
    } catch (err) {
      setError(err.response?.data?.message ?? "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-10"
          style={{
            background: "radial-gradient(ellipse, #139679 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-5"
          style={{
            background: "radial-gradient(ellipse, #4F46E5 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="w-full max-w-sm z-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src={Logo} alt="Ingroove" className="h-10 mb-3" />

          <h1 className="text-xl font-bold ingroove-gradient-text tracking-wide">
            Ingroove
          </h1>
          <p className="text-text-muted text-xs mt-1 tracking-widest uppercase">
            Hub Portal
          </p>
        </div>

        {/* Card */}
        <div className="bg-bg-surface border border-border rounded-2xl p-8 shadow-card">
          <h2 className="text-text-primary text-lg font-semibold mb-1">
            Welcome back
          </h2>
          <p className="text-text-muted text-sm mb-7">
            Sign in to manage your hub
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full bg-bg-base border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  required
                  className="w-full bg-bg-base border border-border rounded-xl pl-9 pr-10 py-2.5 text-sm text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-secondary transition"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full ingroove-gradient-bg text-white font-semibold text-sm py-2.5 rounded-xl hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-glow"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-center text-text-hint text-xs mt-6">
          Ingroove Hub Portal — Not for public use
        </p>
      </div>
    </div>
  );
};

export default Login;
